import {
  Activity,
  createInitialSystemMessage,
  HubblFilter,
  HubblTask,
  User,
} from 'hubbl-shared';
import {Api} from '../../api';
import {HubblBreakDTO, HubblGroupDTO, WebappHubbl} from '../../models';
import {getAuthUserCompanyId} from '../../utils';
import {createAppAsyncThunk} from '../appAsyncThunk';
import {filterIncomingPendingHubbles} from '../utils/filterIncomingPendingHubbles';
import {UserSelector} from '../user/UserSelector';
import {CompanySelector} from '../company/CompanySelector';

interface FetchHubblesResponse {
  hubbles: WebappHubbl[];

  isAppend?: boolean;
}

interface CreateHubblGroupData {
  title: string;
  description: string;
  activity: string;
  emoji: string;
  startDateTime: string;
  endDateTime?: string;
  maxParticipants: number | null;
  location?: string;
  extraDetails?: string;
  invitedUsers: User[];
  filters: HubblFilter[];
  officeId?: string;
  spaceId?: string;
  isRepeatEnabled: boolean;
  tasks: HubblTask[];
}

interface CreateHubblConnectData {
  activities: Activity[];
  startDateTime: string;
  endDateTime?: string;
  isRepeatEnabled: boolean;
  invitedUsers: User[];
  filters: HubblFilter[];
  officeId?: string;
  spaceId?: string;
  tasks: HubblTask[];
}

interface HubblOperationData {
  hubblId: string;
  user?: User;
}

// Fetch pending hubbles (available to join)
export const fetchPendingHubbles = createAppAsyncThunk<
  FetchHubblesResponse,
  {userId: string; isAppend?: boolean}
>(
  'hubbl/fetchPendingHubbles',
  async ({userId, isAppend = false}, {getState}) => {
    const companyId = await getAuthUserCompanyId();
    const response = await Api.hubbl.getPendingHubbles(companyId, userId);
    const state = getState();
    const user = UserSelector.getUserDetails(state);

    const company = CompanySelector.getCompanyDetails(state);
    if (!user || !company) throw new Error('User or company not found');

    const filteredPendingHubbles = filterIncomingPendingHubbles(
      user,
      response,
      company,
    );

    return {
      hubbles: filteredPendingHubbles,
      isAppend,
    };
  },
);

// Fetch scheduled hubbles (user has joined)
export const fetchScheduledHubbles = createAppAsyncThunk<
  WebappHubbl[],
  {userId: string}
>('hubbl/fetchScheduledHubbles', async ({userId}) => {
  const companyId = await getAuthUserCompanyId();

  return new Promise((resolve, reject) => {
    const unsubscribe = Api.hubbl.getScheduledHubbles(
      companyId,
      userId,
      hubbles => {
        unsubscribe();
        resolve(hubbles);
      },
    );
  });
});

// Fetch pending breaks (available to accept)
export const fetchPendingBreaks = createAppAsyncThunk<
  HubblBreakDTO[],
  {userId: string}
>('hubbl/fetchPendingBreaks', async ({userId}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  const response = await Api.breaks.getPendingBreaks(companyId, userId);
  const state = getState();
  const user = UserSelector.getUserDetails(state);

  const company = CompanySelector.getCompanyDetails(state);
  if (!user || !company) throw new Error('User or company not found');

  const filteredPendingHubbles = filterIncomingPendingHubbles(
    user,
    response,
    company,
  );

  return filteredPendingHubbles;
});

// Fetch scheduled breaks (user has accepted)
export const fetchScheduledBreaks = createAppAsyncThunk<
  HubblBreakDTO[],
  {userId: string}
>('hubbl/fetchScheduledBreaks', async ({userId}) => {
  const companyId = await getAuthUserCompanyId();
  const response = await Api.breaks.getScheduledBreaks(companyId, userId);

  return response;
});

// Create hubbl group
export const createHubblGroup = createAppAsyncThunk<
  HubblGroupDTO,
  CreateHubblGroupData
>('hubbl/createHubblGroup', async (groupData, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  const state = getState();
  const user = state.user.userDetails;
  if (!user) throw new Error('User not found');

  const initialMessage = createInitialSystemMessage();
  const requestData = {
    ...groupData,
    creator: user,
    messages: [initialMessage],
  };

  return await Api.hubbl.createHubblGroup(companyId, requestData);
});

// Create hubbl connect (break)
export const createHubblConnect = createAppAsyncThunk<
  HubblBreakDTO,
  CreateHubblConnectData
>('hubbl/createHubblConnect', async (connectData, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  const state = getState();
  const user = state.user.userDetails;
  if (!user) throw new Error('User not found');

  const initialMessage = createInitialSystemMessage();
  return await Api.breaks.createBreak(companyId, {
    ...connectData,
    userCreator: user,
    messages: [initialMessage],
  });
});

// Join hubbl group
export const joinHubblGroup = createAppAsyncThunk<
  Partial<HubblGroupDTO>,
  HubblOperationData
>('hubbl/joinHubblGroup', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to join hubbl group');

  const companyId = await getAuthUserCompanyId();
  await Api.hubbl.joinHubblGroup(companyId, hubblId, user);

  // Return updated hubbl data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

// Leave hubbl group
export const leaveHubblGroup = createAppAsyncThunk<
  Partial<HubblGroupDTO>,
  HubblOperationData
>('hubbl/leaveHubblGroup', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to leave hubbl group');

  const companyId = await getAuthUserCompanyId();
  await Api.hubbl.leaveHubblGroup(companyId, hubblId, user.id);

  // Return updated hubbl data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

// Decline hubbl
export const declineHubbl = createAppAsyncThunk<
  Partial<HubblGroupDTO | HubblBreakDTO>,
  HubblOperationData
>('hubbl/declineHubbl', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to decline hubbl');

  const companyId = await getAuthUserCompanyId();
  await Api.hubbl.declineHubbl(companyId, hubblId, user);

  // Return updated hubbl data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

// Accept break (for connects)
export const acceptBreak = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  HubblOperationData
>('hubbl/acceptBreak', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to accept break');

  const companyId = await getAuthUserCompanyId();
  await Api.breaks.acceptBreak(companyId, hubblId, user);

  // Return updated break data
  const updatedBreak = await Api.breaks.getBreak(companyId, hubblId);
  return updatedBreak || {};
});

// Decline break (for connects)
export const declineBreak = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  HubblOperationData
>('hubbl/declineBreak', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to decline break');

  const companyId = await getAuthUserCompanyId();
  await Api.breaks.declineBreak(companyId, hubblId, user);

  // Return updated break data
  const updatedBreak = await Api.breaks.getBreak(companyId, hubblId);
  return updatedBreak || {};
});

// Undecline hubbl (for groups)
export const undeclineHubbl = createAppAsyncThunk<
  Partial<HubblGroupDTO | HubblBreakDTO>,
  HubblOperationData
>('hubbl/undeclineHubbl', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to undecline hubbl');

  const companyId = await getAuthUserCompanyId();
  await Api.hubbl.undeclineHubbl(companyId, hubblId, user);

  // Return updated hubbl data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

// Undecline break (for connects)
export const undeclineBreak = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  HubblOperationData
>('hubbl/undeclineBreak', async ({hubblId, user}) => {
  if (!user) throw new Error('User is required to undecline break');

  const companyId = await getAuthUserCompanyId();
  await Api.breaks.undeclineBreak(companyId, hubblId, user);

  // Return updated break data
  const updatedBreak = await Api.breaks.getBreak(companyId, hubblId);
  return updatedBreak || {};
});

// Task management thunks for HubblGroups
export const addHubblGroupTask = createAppAsyncThunk<
  Partial<HubblGroupDTO>,
  {hubblId: string; taskTitle: string}
>('hubbl/addHubblGroupTask', async ({hubblId, taskTitle}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  const state = getState();
  const user = state.user.userDetails;
  if (!user) throw new Error('User not found');

  await Api.hubbl.addHubblTask(companyId, hubblId, taskTitle, user.id);

  // Fetch the updated hubbl to get the latest data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

export const updateHubblGroupTask = createAppAsyncThunk<
  Partial<HubblGroupDTO>,
  {hubblId: string; taskId: string; updates: Partial<HubblTask>}
>(
  'hubbl/updateHubblGroupTask',
  async ({hubblId, taskId, updates}, {getState}) => {
    const companyId = await getAuthUserCompanyId();
    await Api.hubbl.updateHubblTask(companyId, hubblId, taskId, updates);

    // Fetch the updated hubbl to get the latest data
    const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
    return updatedHubbl || {};
  },
);

export const deleteHubblGroupTask = createAppAsyncThunk<
  Partial<HubblGroupDTO>,
  {hubblId: string; taskId: string}
>('hubbl/deleteHubblGroupTask', async ({hubblId, taskId}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  await Api.hubbl.deleteHubblTask(companyId, hubblId, taskId);

  // Fetch the updated hubbl to get the latest data
  const updatedHubbl = await Api.hubbl.getHubbl(companyId, hubblId);
  return updatedHubbl || {};
});

// Task management thunks for Breaks/Connects
export const addBreakTask = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  {breakId: string; taskTitle: string}
>('hubbl/addBreakTask', async ({breakId, taskTitle}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  const state = getState();
  const user = state.user.userDetails;
  if (!user) throw new Error('User not found');

  await Api.breaks.addBreakTask(companyId, breakId, taskTitle, user.id);

  // Fetch the updated break to get the latest data
  const updatedBreak = await Api.breaks.getBreak(companyId, breakId);
  return updatedBreak || {};
});

export const updateBreakTask = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  {breakId: string; taskId: string; updates: Partial<HubblTask>}
>('hubbl/updateBreakTask', async ({breakId, taskId, updates}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  await Api.breaks.updateBreakTask(companyId, breakId, taskId, updates);

  // Fetch the updated break to get the latest data
  const updatedBreak = await Api.breaks.getBreak(companyId, breakId);
  return updatedBreak || {};
});

export const deleteBreakTask = createAppAsyncThunk<
  Partial<HubblBreakDTO>,
  {breakId: string; taskId: string}
>('hubbl/deleteBreakTask', async ({breakId, taskId}, {getState}) => {
  const companyId = await getAuthUserCompanyId();
  await Api.breaks.deleteBreakTask(companyId, breakId, taskId);

  // Fetch the updated break to get the latest data
  const updatedBreak = await Api.breaks.getBreak(companyId, breakId);
  return updatedBreak || {};
});
